import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { AppDispatch } from "../../../../dataManagement/uiDb/UiDb";
import { createOrder } from "../../../../services/thunks/OrderThunk";
import { FulfillmentType } from "../../../../interfaces/OrderObject";
import { useNotification } from "../../contexts/NotificationContext";
import { NotificationType } from "../../../../constants/Enums";
import ConfirmModal from "../../ConfirmModal/ConfirmModal";
import "./styles/NineFindsOrderButton.css";

/** Strip the origin so the stored collection link is portable across environments. */
function toRelativeCollectionUrl(url: string): string {
  try {
    const u = new URL(url);
    return u.pathname + u.search + u.hash;
  } catch {
    return url;
  }
}

interface NineFindsOrderButtonProps {
  businessId?: string;
  /** The collection URL the order references (same value used for sharing). */
  collectionUrl: string;
  totalPrice: number;
  /** Resolved buyer phone (signed-in users only). */
  buyerPhone: string | null;
  /** Fixed by the entry point (On-premise vs Delivery column). */
  fulfillmentType: FulfillmentType;
  /** DELIVERY only. */
  deliveryAddress?: string;
  /** DELIVERY only. */
  deliveryPin?: { lat: number; lng: number } | null;
  isLoggedIn: boolean;
  /** When true, a precise pin isn't required for delivery (maps unavailable). */
  pinOptional?: boolean;
  onRequireLogin: () => void;
  onClose: () => void;
}

/**
 * "Order via Nine" — places an in-app order for a single, fixed fulfillment type
 * (the column the user chose decides DELIVERY vs ON_PREMISE). Handles the in-flight
 * guard, failure toast, and on success offers to jump to the business's Orders tab.
 */
const NineFindsOrderButton: React.FC<NineFindsOrderButtonProps> = ({
  businessId,
  collectionUrl,
  totalPrice,
  buyerPhone,
  fulfillmentType,
  deliveryAddress,
  deliveryPin,
  isLoggedIn,
  pinOptional = false,
  onRequireLogin,
  onClose,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const { triggerNotification } = useNotification();

  const [placing, setPlacing] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  if (!businessId) return null;

  const needsDelivery = fulfillmentType === "DELIVERY";
  const address = (deliveryAddress ?? "").trim();
  const deliveryReady =
    !needsDelivery || (address.length > 0 && (deliveryPin != null || pinOptional));
  const disabled = placing || (isLoggedIn && !deliveryReady);

  const handlePlace = async () => {
    if (!isLoggedIn) {
      onRequireLogin();
      return;
    }
    if (!deliveryReady || placing) return;
    setPlacing(true);
    try {
      await dispatch(
        createOrder({
          businessId,
          collectionUrl: toRelativeCollectionUrl(collectionUrl),
          totalPrice,
          fulfillmentType,
          buyerPhone: buyerPhone ?? "",
          ...(needsDelivery
            ? { deliveryAddress: address, deliveryPin: deliveryPin ?? undefined }
            : {}),
        })
      ).unwrap();
      setShowConfirm(true);
    } catch (err: any) {
      triggerNotification(
        err?.message || "Could not place your order. Please try again.",
        NotificationType.ERROR
      );
    } finally {
      setPlacing(false);
    }
  };

  return (
    <>
      <button
        type="button"
        className="nf-order__btn nf-order__btn--full"
        onClick={handlePlace}
        disabled={disabled}
      >
        <img src="/nine_logo_vector_4.svg" alt="" className="nf-order__logo" />
        <span>{placing ? "Placing order..." : "Order via Nine"}</span>
      </button>

      {isLoggedIn && needsDelivery && !deliveryReady && (
        <p className="nf-order__hint">
          Add your delivery address{pinOptional ? "" : " and pin"} above to order.
        </p>
      )}

      <ConfirmModal
        isOpen={showConfirm}
        title="Order placed!"
        message="Your order has been sent to the business. Would you like to view your orders?"
        confirmLabel="View orders"
        cancelLabel="Not now"
        onConfirm={() => {
          setShowConfirm(false);
          onClose();
          navigate(`/business/${businessId}?tab=orders`);
        }}
        onCancel={() => {
          setShowConfirm(false);
          onClose();
        }}
      />
    </>
  );
};

export default NineFindsOrderButton;
